(ns minorodata.components.transformations.sql-generator.sidebar
  (:require
   ["lucide-react" :refer [Box ChevronDown ChevronRight
                           Folder ListTree Table]]
   ["react" :as react]
   [applied-science.js-interop :as js-interop]
   [clojure.set]
   [clojure.string :as c.string]
   [minorodata.app.handlers :refer [dispatch]]
   [minorodata.components.spinner.core :as c.spinner]
   [minorodata.components.transformations.schema-tree :as c.schema-tree]
   [minorodata.json-flattening.model :refer [->json-flattening]]
   [re-posh.core :as re-posh]
   [ui.button]
   [ui.chip]
   [ui.dialog]
   [ui.fields :as fields :refer [input-class]]
   [ui.popover]
   [ui.tree :as ui.tree]
   [ui.typography :as typography]
   [ui.utils :refer [clsx]]))



(defn generate-table-name [node]
  (str (:table-cat node) "." (:table-schem node) "." (:table-name node)))

(defn title-sorting-fn [title-key]
  (fn [a-node b-node]
    (let [a-title (js-interop/get a-node title-key)
          b-title (js-interop/get b-node title-key)]
      (compare a-title b-title))))

(defn max-if-sql-max-varchar-size
  "Print \"MAX\" if length is 65535 (redshift limit) or 16777216 (snoflake limit). Otherwise print length."
  [len]
  (if (or (= 65535 len)
          (= 16777216 len))
    "MAX"
    (str len)))


(defn column-type-name [node]
  (let [type-name (js-interop/get node :type-name)
        char-octet-length (js-interop/get node :char-octet-length)]
    (if (and (= "VARCHAR" type-name)
             (some? char-octet-length))
      (str type-name " (" (max-if-sql-max-varchar-size char-octet-length) ")")
      type-name)))


(defn append-checked-columns [{:keys [checked-columns ordered-columns fetch-new-preview]}]
  (let [new-columns (vec (remove #(some #{%} ordered-columns)
                                 checked-columns))

        removed-columns (vec (clojure.set/difference
                              (set ordered-columns)
                              (set checked-columns)))

        ordered-without-removed (vec (remove #(some #{%} removed-columns)
                                             ordered-columns))

        ordered-columns' (vec (concat ordered-without-removed
                                      new-columns))]

    (dispatch [:json-flattening/update (->json-flattening {:ordered-columns ordered-columns'})])
    ;; Prevent a race condition
    (js/setTimeout fetch-new-preview 1000)))


(defn json-tree [{:keys [fetch-new-preview]}]
  (let [checked-columns @(re-posh/subscribe [:json-flattening/prop :json-flattening/checked-columns])
        ordered-columns @(re-posh/subscribe [:json-flattening/prop :json-flattening/ordered-columns])]
    [:div {:class "overflow-hidden grow shrink flex flex-col gap-2"}
     [:f> c.schema-tree/schema-tree {:controls true}]
     [:div {:class "flex justify-end"}
      [ui.button/button
       {:icon-right ChevronRight
        :variant :secondary
        :on-click #(append-checked-columns {:checked-columns checked-columns
                                            :ordered-columns ordered-columns
                                            :fetch-new-preview fetch-new-preview})}
       "Apply selection"]]]))


(defn get-offset [node-type]
  (case node-type
    "catalog" ""
    "schema"  "pl-2"
    "table"   "pl-4"
    "column"  "pl-6"
    0))

(defn node-type [node]
  (cond
    (:column-name node) "column"
    (:table-name node)  "table"
    (:table-schem node) "schema"
    (:table-cat node)   "catalog"
    :else               nil))

(defn type-icon [item-type]
  (case item-type
    "catalog" Folder
    "schema"  ListTree
    "table"   Table
    "column"  Box
    nil))

(defn node-object [node]
  (cond
    (:table-name node)  "columns"
    (:table-schem node) "tables"
    (:table-cat node)   "schemas"
    :else nil))

(defn node-title [node]
  (cond
    (:column-name node) (:column-name node)
    (:table-name node)  (:table-name node)
    (:table-schem node) (:table-schem node)
    (:table-cat node)   (:table-cat node)
    :else nil))

(defn fetch-params [node]
  (cond
    (:column-name node) nil
    (:table-name node)  {:object "columns"
                         :catalog (or (:table-catalog node) (:table-cat node))
                         :schema  (:table-schem node)
                         :table   (:table-name node)}
    (:table-schem node) {:object "tables"
                         :catalog (or (:table-catalog node) (:table-cat node))
                         :schema (:table-schem node)}
    (:table-cat node)   {:object "schemas"
                         :catalog (:table-cat node)}))

(defn node-column-type-name [node]
  (let [type-name (:type-name node)
        char-octet-length (:char-octet-length node)]
    (if (and (= "VARCHAR" type-name)
             (some? char-octet-length))
      (str type-name " (" (max-if-sql-max-varchar-size char-octet-length) ")")
      type-name)))

(defn node-description [node children-count]
  (if (:column-name node)
    (node-column-type-name node)
    (when children-count
      (str children-count " " (node-object node)))))

(defn select-table [selected-table]
  (dispatch [:json-flattening/update (->json-flattening {:selected-table selected-table
                                                         :selected-columns []
                                                         :checked-columns  []
                                                         :code ""
                                                         :data {:preview []}
                                                         :show-select-table? false})])
  (dispatch [:json-flattening.api/fetch-columns (generate-table-name selected-table)])
  (dispatch [:app.events/navigate-query
             {:table-name (generate-table-name selected-table)}]))

(defonce tree-node-open-cache #js {})

(defn ->node-cache-key [node]
  (str (:table-cat node) "." (:table-schem node) "." (:table-name node) "." (:column-name node)))

(defn tree-node [{:keys [node on-open]}]
  (let [type             (node-type node)
        title            (node-title node)
        leaf             (= type "table")
        params           (fetch-params node)
        children         (when-not leaf
                           @(re-posh/subscribe [:sql-metadata-partial/prop [:sql-metadata-partial/params params]
                                                :sql-metadata-partial/metadata]))
        children-count   (some-> children count)
        cache-key        (->node-cache-key node)
        [open on-open-change] (ui.tree/use-cached-open-state tree-node-open-cache cache-key node)]
    [ui.tree/tree-node
     {:open           open
      :on-open-change (fn [v]
                        (when (and v params)
                          (on-open params))
                        (on-open-change v))
      :class          (get-offset type)}
     [ui.tree/tree-row-trigger
      {:leaf     leaf
       :on-click (when leaf (fn [] (select-table node)))}
      [ui.tree/tree-row-icon (type-icon type)]
      [ui.tree/tree-row-label {:title title} title]
      (when-let [secondary (node-description node children-count)]
        [ui.tree/tree-row-secondary-label secondary])]
     (when-not leaf
       (into [ui.tree/tree-node-content]
             (for [s children]
               ^{:key (->node-cache-key s)}
               [:f> tree-node {:on-open on-open
                               :node    s}])))]))

(defn tree-view [{:keys [children]}]
  (let [fetch-catalog-tables #(dispatch [:sql-console.api/fetch-metadata %])]
    (into
     [:div]
     (for [child children]
       [:f> tree-node {:on-open fetch-catalog-tables
                       :node    child}]))))

(defn json-flattening-sidebar-tree [{:keys [catalogs]}]
  (let [dropdown?     @(re-posh/subscribe [:json-flattening/prop :json-flattening/show-select-table?])
        route         @(re-posh/subscribe [:app-route/pull])
        default-table (get-in route [:app-route/query :table-name])]

    (react/useEffect
     (fn []
       (when (and default-table (not dropdown?))
         (let [selected-table (some->> (some-> default-table (c.string/split #"\.") reverse)
                                       (zipmap [:table-name :table-schem :table-cat]))]
           (some-> selected-table select-table)))
       (fn []))
     #js [])

    (let [catalogs (sort (title-sorting-fn :table-cat) catalogs)]
      [tree-view {:children catalogs}])))


(defn json-flattening-sidebar [{:keys [fetch-new-preview lock-table?]}]
  (let [json-flattening-props @(re-posh/subscribe [:json-flattening/pull])
        catalogs              @(re-posh/subscribe [:sql-metadata-partial/prop [:sql-metadata-partial/params {:object "catalogs"}]
                                                   :sql-metadata-partial/metadata])
        loading?              (and (not catalogs) @(re-posh/subscribe [:mold-http/loading? :sql-console.api/fetch-metadata]))
        selected-table        (:json-flattening/selected-table json-flattening-props)
        show-select-table?    (:json-flattening/show-select-table? json-flattening-props)
        show-metadata-tree?   (or (nil? selected-table)
                                  show-select-table?)]

    [:div {:class "flex flex-col gap-4 p-4 h-full"}
     [:div {:class "flex flex-col gap-2"}
      [typography/h3 "Input table"]
      (if lock-table?
        [:input {:value (:table-name selected-table)
                 :disabled true
                 :class (input-class {:disabled true})}]
        [:button {:class "border border-kleene-neutral-03 dark:border-kleene-dark-03 text-left flex py-2 pl-4 pr-2  rounded-lg cursor-pointer"
                  :disabled (nil? selected-table)
                  :on-click #(dispatch [:json-flattening/update (->json-flattening {:show-select-table? (not show-select-table?)})])}
         [:div {:class "flex-1 overflow-hidden text-ellipsis leading-6"}
          (or (:table-name selected-table)
              [:div {:class "text-kleene-text-secondary"} "Select an input table"])]

         [:> ChevronDown {:class (clsx "transition-transform"
                                       {"rotate-180" show-select-table?})}]])]

     (cond
       loading?
       [:div {:class "h-full w-full flex items-center justify-center"}
        [c.spinner/spinner-lucide {:size 50}]]

       show-metadata-tree?
       [:div {:class "grow p-2 overflow-auto border border-kleene-neutral-03 dark:border-kleene-dark-03 rounded-lg"}
        [:f> json-flattening-sidebar-tree {:catalogs catalogs}]]

       :else
       [json-tree {:catalog catalogs :fetch-new-preview fetch-new-preview}])]))